// Build a seamless full-size image from a LEGACY cube capture's face frames (captures with no stored
// equirect panorama). Modern equirect captures are untouched — their provenance references the equirect
// file, not a cube face, so none of this runs for them.
//
// Two forms (equirect to follow): a 360° horizontal STRIP from the four side faces — the common
// side-only case — is implemented here. Each face is trimmed to its central 90° (from the "_fNN_" FOV
// baked into its filename, e.g. ..._f92_left.png → 92°) so neighbours tile with no overlap.

// Continuous horizontal order around the rig (world +Y, +X, -Y, -X) so adjacent faces are adjacent in
// the strip. Up/down are not part of the strip.
const RING = ['front', 'right', 'back', 'left'];

// Face suffix on a frame filename → canonical label. Matches the viewer's naming (named faces + the
// OpenGL px/nx/py/ny/pz/nz axes, pz == front).
const CUBE_FACE_RE = /[_-](front|back|left|right|up|down|top|bottom|px|nx|py|ny|pz|nz)(?=[_.-]|$)/i;
const FACE_ALIAS = {
  front: 'front', back: 'back', left: 'left', right: 'right', up: 'up', top: 'up', down: 'down',
  bottom: 'down', pz: 'front', nz: 'back', px: 'right', nx: 'left', py: 'up', ny: 'down',
};

// The cube-face label for a frame filename, or null if it carries no face suffix (e.g. an equirect or a
// mono frame — both of which must NOT be treated as a legacy cube face).
export function cubeFaceLabelOf(name) {
  const m = CUBE_FACE_RE.exec(stemOf(name));
  return m ? FACE_ALIAS[m[1].toLowerCase()] : null;
}

// The capture base shared by a rig's six faces: the filename stem with its face suffix removed.
export function cubeBaseOf(name) {
  return stemOf(name).replace(CUBE_FACE_RE, '');
}

// Source FOV (degrees) baked into the filename as "_fNN_" (legacy faces carry no intrinsics), else 90.
export function faceFovDegOf(name) {
  const m = /[_-]f(\d{2,3})(?=[_.-]|$)/i.exec(stemOf(name));
  if (m) { const d = Number(m[1]); if (d >= 60 && d <= 170) return d; }
  return 90;
}

// Full horizontal FOV (degrees) from a camera helper's intrinsics — the authoritative, filename-independent
// source. Prefers the principal point (optical centre ≈ half-sensor) over imageWidth, which can be an odd
// value that throws the FOV off. Reads both the mapped-instance names (focalX/opticCenterX) and the raw
// DTO names (cameraFocalX/cameraOpticalCenterX). Returns null when no usable intrinsics are present.
export function fovDegFromHelper(h) {
  if (!h) return null;
  const fx = Number(h.cameraFocalX ?? h.focalX);
  const ppx = Number(h.cameraOpticalCenterX ?? h.opticCenterX);
  const w = Number(h.imageWidth);
  const half = fx && ppx ? Math.atan(ppx / fx) : fx && w ? Math.atan(w / (2 * fx)) : null;
  return half ? (half * 360 / Math.PI) : null;
}

function stemOf(name) {
  return (name || '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '');
}

// Face → world frame, matching the viewer's CUBE_FACE_LAYOUT (the legacy cube's actual orientation).
// front=+Y, back=-Y, right=+X, left=-X, up=+Z, down=-Z. Both the equirect we generate and the box we
// place on it use THIS frame, so they're self-consistent (the equirect-anchored FACE_RING is NOT used).
const CUBE_FACE_LAYOUT = {
  front: { dir: [0, 1, 0], up: [0, 0, 1] },
  back: { dir: [0, -1, 0], up: [0, 0, 1] },
  right: { dir: [1, 0, 0], up: [0, 0, 1] },
  left: { dir: [-1, 0, 0], up: [0, 0, 1] },
  up: { dir: [0, 0, 1], up: [0, -1, 0] },
  down: { dir: [0, 0, -1], up: [0, 1, 0] },
};

const vCross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const vNorm = (a) => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };

// Camera basis for a face: forward = dir, with right (X) and up (Y) matching a THREE lookAt(dir) camera,
// so the per-pixel projection below agrees with the face IMAGE's pixel orientation.
function faceBasis(label) {
  const L = CUBE_FACE_LAYOUT[label];
  if (!L) return null;
  const dir = vNorm(L.dir);
  const X = vNorm(vCross(dir, L.up));          // camera right = normalize(dir × up)
  const Z = [-dir[0], -dir[1], -dir[2]];       // camera looks along -Z = -(-dir) = dir
  const Y = vCross(Z, X);                       // camera up
  return { dir, X, Y };
}

// Load a face, trim to its central 90° (via its _fNN_ FOV), downscale to a working square, and return
// its pixels + basis for equirect sampling.
async function faceSample(face) {
  const basis = faceBasis(face.label);
  if (!basis) return null;
  const img = await loadImage(face.url);
  const W = img.naturalWidth, H = img.naturalHeight;
  const k = keepFrac(face.fovDeg);
  const cw = W * k, ch = H * k, sx = (W - cw) / 2, sy = (H - ch) / 2;
  const work = Math.max(1, Math.min(1024, Math.round(Math.min(cw, ch))));
  const c = document.createElement('canvas');
  c.width = work; c.height = work;
  c.getContext('2d').drawImage(img, sx, sy, cw, ch, 0, 0, work, work);
  const id = c.getContext('2d').getImageData(0, 0, work, work);
  return { data: id.data, w: work, h: work, basis, label: face.label, origW: W, origH: H, keep: k };
}

/**
 * Rebuild an equirectangular panorama on the fly from all 6 cube faces (≥4 accepted; missing faces leave
 * neutral regions). Self-consistent with `mapBox`, which places a single-face detection box onto the
 * generated equirect (returns the box in equirect pixels; may wrap past the right edge, which the viewer
 * splits at the seam). Equirect convention: u=0.5 → +Y (front), v=0 → +Z (zenith).
 * @param {{label:string,url:string,fovDeg:number}[]} faces
 * @returns {Promise<null|{dataUrl:string,width:number,height:number,faceLabels:string[],mapBox:Function}>}
 */
export async function buildCubeEquirect(faces, { outWidth = 2048 } = {}) {
  const samples = new Map();
  for (const f of faces || []) {
    if (f && CUBE_FACE_LAYOUT[f.label] && f.url && !samples.has(f.label)) {
      const s = await faceSample(f);
      if (s) samples.set(f.label, s);
    }
  }
  if (samples.size < 4) return null;

  const outW = outWidth, outH = Math.round(outWidth / 2);
  const out = new Uint8ClampedArray(outW * outH * 4);
  const list = [...samples.values()];
  const TWO_PI = Math.PI * 2;

  for (let py = 0; py < outH; py++) {
    const v = (py + 0.5) / outH, phi = v * Math.PI, sp = Math.sin(phi), cp = Math.cos(phi);
    for (let px = 0; px < outW; px++) {
      const u = (px + 0.5) / outW, th = (u - 0.5) * TWO_PI;
      const dx = sp * Math.sin(th), dy = sp * Math.cos(th), dz = cp;   // view direction
      let best = null, bestDot = 1e-3;
      for (const s of list) {
        const dd = dx * s.basis.dir[0] + dy * s.basis.dir[1] + dz * s.basis.dir[2];
        if (dd > bestDot) { bestDot = dd; best = s; }
      }
      const o = (py * outW + px) * 4;
      if (!best) { out[o] = 40; out[o + 1] = 44; out[o + 2] = 48; out[o + 3] = 255; continue; }
      let nx = (dx * best.basis.X[0] + dy * best.basis.X[1] + dz * best.basis.X[2]) / bestDot;
      let ny = (dx * best.basis.Y[0] + dy * best.basis.Y[1] + dz * best.basis.Y[2]) / bestDot;
      let sxu = (nx + 1) / 2, syu = (1 - ny) / 2;
      sxu = sxu < 0 ? 0 : sxu > 1 ? 1 : sxu;
      syu = syu < 0 ? 0 : syu > 1 ? 1 : syu;
      const ix = Math.min(best.w - 1, (sxu * (best.w - 1)) | 0);
      const iy = Math.min(best.h - 1, (syu * (best.h - 1)) | 0);
      const si = (iy * best.w + ix) * 4;
      out[o] = best.data[si]; out[o + 1] = best.data[si + 1]; out[o + 2] = best.data[si + 2]; out[o + 3] = 255;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW; canvas.height = outH;
  canvas.getContext('2d').putImageData(new ImageData(out, outW, outH), 0, 0);

  // Single-face box (original face pixels) → equirect pixels, via the same frame the equirect was built in.
  const mapBox = (label, box) => {
    const s = samples.get(label);
    if (!s || !box) return null;
    const { basis, origW, origH, keep } = s;
    const cw = origW * keep, ch = origH * keep, cropL = (origW - cw) / 2, cropT = (origH - ch) / 2;
    const us = [], vs = [];
    const xs = [box.left, box.left + box.width / 2, box.left + box.width];
    const ys = [box.top, box.top + box.height / 2, box.top + box.height];
    for (const fx of xs) for (const fy of ys) {
      const ndcx = ((fx - cropL) / cw) * 2 - 1;        // original px → central-90° uv → ndc
      const ndcy = 1 - ((fy - cropT) / ch) * 2;
      let dX = basis.dir[0] + ndcx * basis.X[0] + ndcy * basis.Y[0];
      let dY = basis.dir[1] + ndcx * basis.X[1] + ndcy * basis.Y[1];
      let dZ = basis.dir[2] + ndcx * basis.X[2] + ndcy * basis.Y[2];
      const l = Math.hypot(dX, dY, dZ) || 1; dX /= l; dY /= l; dZ /= l;
      let uu = Math.atan2(dX, dY) / TWO_PI + 0.5; uu = ((uu % 1) + 1) % 1;
      us.push(uu);
      vs.push(Math.acos(Math.max(-1, Math.min(1, dZ))) / Math.PI);
    }
    let minU = Math.min(...us), maxU = Math.max(...us);
    if (maxU - minU > 0.5) {                            // straddles the ±180° seam → unwrap the left side
      for (let i = 0; i < us.length; i++) if (us[i] < 0.5) us[i] += 1;
      minU = Math.min(...us); maxU = Math.max(...us);
    }
    const minV = Math.min(...vs), maxV = Math.max(...vs);
    return { left: minU * outW, top: minV * outH, width: (maxU - minU) * outW, height: (maxV - minV) * outH };
  };

  return { dataUrl: canvas.toDataURL('image/webp', 0.92), width: outW, height: outH, faceLabels: [...samples.keys()], mapBox };
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = url;
  });
}

// Fraction of a face (per axis) to KEEP when trimming its `fovDeg` down to the central 90° so the cube
// tiles seamlessly. A 92° face keeps ~0.966 (≈1.7% trimmed off each edge).
function keepFrac(fovDeg) {
  const half = (Math.max(60, Math.min(170, fovDeg)) * Math.PI / 180) / 2;
  return Math.min(1, Math.tan(Math.PI / 4) / Math.tan(half));
}

/**
 * Build a 360° horizontal strip from the side faces of one capture.
 * @param {{label:string,url:string,fovDeg:number}[]} faces  the capture's faces (up/down are ignored)
 * @returns {Promise<null|{dataUrl:string,width:number,height:number,faceLabels:string[],
 *           mapBox:(label:string,box:{left:number,top:number,width:number,height:number})=>
 *             ({left:number,top:number,width:number,height:number}|null)}>}
 *   `mapBox` maps a box in ORIGINAL face pixels (top-left origin) to strip pixels, or null if that face
 *   isn't in the strip (e.g. the detection's face is up/down).
 */
export async function buildCubeStrip(faces) {
  const byLabel = new Map();
  for (const f of faces || []) {
    if (f && RING.includes(f.label) && f.url && !byLabel.has(f.label)) byLabel.set(f.label, f);
  }
  const present = RING.filter((l) => byLabel.has(l));
  if (!present.length) return null;

  const imgs = new Map();
  await Promise.all(present.map(async (l) => { imgs.set(l, await loadImage(byLabel.get(l).url)); }));

  // One square cell per face, sized to the smallest cropped face so none is upscaled.
  let cell = Infinity;
  for (const l of present) {
    const im = imgs.get(l);
    const k = keepFrac(byLabel.get(l).fovDeg);
    cell = Math.min(cell, Math.round(Math.min(im.naturalWidth, im.naturalHeight) * k));
  }
  if (!Number.isFinite(cell) || cell <= 0) return null;

  const slot = new Map();
  present.forEach((l, i) => slot.set(l, i));

  const canvas = document.createElement('canvas');
  canvas.width = cell * present.length;
  canvas.height = cell;
  const ctx = canvas.getContext('2d');

  for (const l of present) {
    const im = imgs.get(l);
    const k = keepFrac(byLabel.get(l).fovDeg);
    const W = im.naturalWidth, H = im.naturalHeight;
    const cw = W * k, ch = H * k;               // central crop to 90°
    const sx = (W - cw) / 2, sy = (H - ch) / 2;
    ctx.drawImage(im, sx, sy, cw, ch, slot.get(l) * cell, 0, cell, cell);
  }

  const mapBox = (label, box) => {
    if (!slot.has(label) || !box) return null;
    const im = imgs.get(label);
    const k = keepFrac(byLabel.get(label).fovDeg);
    const W = im.naturalWidth, H = im.naturalHeight;
    const cw = W * k, ch = H * k;
    const cropL = (W - cw) / 2, cropT = (H - ch) / 2;
    return {
      left: slot.get(label) * cell + (box.left - cropL) / cw * cell,
      top: (box.top - cropT) / ch * cell,
      width: box.width / cw * cell,
      height: box.height / ch * cell,
    };
  };

  return { dataUrl: canvas.toDataURL('image/webp', 0.92), width: canvas.width, height: canvas.height, faceLabels: present, mapBox };
}
